import React, { useMemo } from 'react';
import styles from './AddEvidencePopup.module.css';
import { ChevronIcon, FolderIcon } from '../../../Routes/Library/LibraryIcons';
import { LibraryFolder, LibraryPortfolio } from '../../../Types/Library/Library';

type LibraryExplorerTreeProps = {
  portfolios: LibraryPortfolio[];
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectedPortfolioId: string;
  selectedFolderId: string;
  onOpenPortfolio: (portfolioId: string) => void;
  onOpenFolder: (portfolioId: string, folderId: string) => void;
  /** Portfolio ids the user can open but not save into. */
  viewOnlyIds?: Set<string>;
};

const LibraryExplorerTree: React.FC<LibraryExplorerTreeProps> = ({
  portfolios,
  expanded,
  setExpanded,
  selectedPortfolioId,
  selectedFolderId,
  onOpenPortfolio,
  onOpenFolder,
  viewOnlyIds,
}) => {
  const expandableKeys = useMemo(() => {
    const keys: string[] = [];
    const walk = (folders: LibraryFolder[] | undefined) => {
      for (const folder of folders ?? []) {
        if ((folder.folders ?? []).length > 0) {
          keys.push(`f:${folder.Id}`);
          walk(folder.folders);
        }
      }
    };
    for (const portfolio of portfolios) {
      if ((portfolio.folders ?? []).length > 0) keys.push(`p:${portfolio.id}`);
      walk(portfolio.folders);
    }
    return keys;
  }, [portfolios]);

  const allExpanded = expandableKeys.length > 0 && expandableKeys.every((key) => expanded.has(key));

  const toggle = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside className={styles.explorerTree}>
      <div className={styles.treeLabelBar}>
        <span>Folders</span>
        {expandableKeys.length > 0 && (
          <button
            type="button"
            className={styles.treeExpandAll}
            onClick={() => setExpanded(allExpanded ? new Set() : new Set(expandableKeys))}
          >
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>
      {portfolios.map((portfolio) => (
        <ExplorerPortfolio
          key={portfolio.id}
          portfolio={portfolio}
          expanded={expanded}
          selectedPortfolioId={selectedPortfolioId}
          selectedFolderId={selectedFolderId}
          onToggle={toggle}
          onOpenPortfolio={onOpenPortfolio}
          onOpenFolder={onOpenFolder}
          viewOnly={viewOnlyIds?.has(portfolio.id) ?? false}
        />
      ))}
    </aside>
  );
};

const ExplorerPortfolio = ({
  portfolio,
  expanded,
  selectedPortfolioId,
  selectedFolderId,
  onToggle,
  onOpenPortfolio,
  onOpenFolder,
  viewOnly = false,
}: {
  portfolio: LibraryPortfolio;
  expanded: Set<string>;
  selectedPortfolioId: string;
  selectedFolderId: string;
  onToggle: (key: string) => void;
  onOpenPortfolio: (portfolioId: string) => void;
  onOpenFolder: (portfolioId: string, folderId: string) => void;
  viewOnly?: boolean;
}) => {
  const key = `p:${portfolio.id}`;
  const hasChildren = (portfolio.folders ?? []).length > 0;
  const isOpen = hasChildren && expanded.has(key);
  const active = selectedPortfolioId === portfolio.id && !selectedFolderId;
  return (
    <div>
      <div className={`${styles.treeRow} ${active ? styles.treeRowActive : ''}`}>
        <div className={styles.treeItem}>
          <button
            type="button"
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
            aria-label={hasChildren ? (isOpen ? `Collapse ${portfolio.title}` : `Expand ${portfolio.title}`) : undefined}
            disabled={!hasChildren}
            onClick={() => hasChildren && onToggle(key)}
          >
            {hasChildren ? <ChevronIcon size={12} /> : <span className={styles.chevronSpacer} />}
          </button>
          <button type="button" className={styles.treeOpen} onClick={() => onOpenPortfolio(portfolio.id)}>
            <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
            <span className={styles.treeLabel}>{portfolio.title}</span>
            {viewOnly && <span className={styles.viewOnlyTag}>View only</span>}
          </button>
        </div>
      </div>
      {isOpen && (portfolio.folders ?? []).map((folder) => (
        <ExplorerFolder
          key={folder.Id}
          portfolioId={portfolio.id}
          folder={folder}
          depth={1}
          expanded={expanded}
          selectedPortfolioId={selectedPortfolioId}
          selectedFolderId={selectedFolderId}
          onToggle={onToggle}
          onOpenFolder={onOpenFolder}
        />
      ))}
    </div>
  );
};

const ExplorerFolder = ({
  portfolioId,
  folder,
  depth,
  expanded,
  selectedPortfolioId,
  selectedFolderId,
  onToggle,
  onOpenFolder,
}: {
  portfolioId: string;
  folder: LibraryFolder;
  depth: number;
  expanded: Set<string>;
  selectedPortfolioId: string;
  selectedFolderId: string;
  onToggle: (key: string) => void;
  onOpenFolder: (portfolioId: string, folderId: string) => void;
}) => {
  const key = `f:${folder.Id}`;
  const children = folder.folders ?? [];
  const hasChildren = children.length > 0;
  const isOpen = hasChildren && expanded.has(key);
  const active = selectedPortfolioId === portfolioId && selectedFolderId === folder.Id;
  return (
    <div className={styles.treeBranch}>
      <div className={`${styles.treeRow} ${active ? styles.treeRowActive : ''}`}>
        <div className={styles.treeItem} style={{ paddingLeft: 8 + depth * 14 }}>
          <button
            type="button"
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
            aria-label={hasChildren ? (isOpen ? `Collapse ${folder.Title}` : `Expand ${folder.Title}`) : undefined}
            disabled={!hasChildren}
            onClick={() => hasChildren && onToggle(key)}
          >
            {hasChildren ? <ChevronIcon size={12} /> : <span className={styles.chevronSpacer} />}
          </button>
          <button type="button" className={styles.treeOpen} onClick={() => onOpenFolder(portfolioId, folder.Id)}>
            <span className={styles.folderGlyph}><FolderIcon size={16} /></span>
            <span className={styles.treeLabel}>{folder.Title}</span>
          </button>
        </div>
      </div>
      {isOpen && children.map((child) => (
        <ExplorerFolder
          key={child.Id}
          portfolioId={portfolioId}
          folder={child}
          depth={depth + 1}
          expanded={expanded}
          selectedPortfolioId={selectedPortfolioId}
          selectedFolderId={selectedFolderId}
          onToggle={onToggle}
          onOpenFolder={onOpenFolder}
        />
      ))}
    </div>
  );
};

export default LibraryExplorerTree;
